import { cn } from '../lib/utils';

export type ShogunSpinnerSize = 'sm' | 'md' | 'lg';

const SIZE: Record<ShogunSpinnerSize, { box: string; outer: string; inner: string; glyph: string | null }> = {
  sm: { box: 'h-5 w-5', outer: 'border-2', inner: 'inset-1 border-2', glyph: null },
  md: { box: 'h-12 w-12', outer: 'border-[3px]', inner: 'inset-2 border-[3px]', glyph: 'text-lg' },
  lg: { box: 'h-24 w-24', outer: 'border-4', inner: 'inset-3 border-4', glyph: 'text-3xl' },
};

export function ShogunSpinner({ size = 'md', className }: { size?: ShogunSpinnerSize; className?: string }) {
  const s = SIZE[size];
  return (
    <div className={cn('relative shrink-0', s.box, className)} aria-hidden="true" data-testid="shogun-spinner">
      <div
        className={cn(
          'absolute inset-0 animate-spin rounded-full border-muted border-t-primary motion-reduce:animate-none',
          s.outer,
        )}
      />
      <div
        className={cn(
          'absolute animate-spin rounded-full border-transparent border-b-primary/60 [animation-direction:reverse] [animation-duration:1.6s] motion-reduce:animate-none',
          s.inner,
        )}
      />
      {s.glyph && (
        <div
          className={cn(
            'absolute inset-0 flex items-center justify-center animate-bounce motion-reduce:animate-none',
            s.glyph,
          )}
        >
          🥢
        </div>
      )}
    </div>
  );
}

export interface LoadingStateProps {
  label: string;
  variant?: 'block' | 'inline';
  className?: string;
  'data-testid'?: string;
}

export function LoadingState({ label, variant = 'block', className, 'data-testid': testId }: LoadingStateProps) {
  if (variant === 'inline') {
    return (
      <div
        role="status"
        aria-live="polite"
        data-testid={testId}
        className={cn('flex items-center gap-2 text-xs text-muted-foreground', className)}
      >
        <ShogunSpinner size="sm" />
        <span>{label}</span>
      </div>
    );
  }
  return (
    <div
      role="status"
      aria-live="polite"
      data-testid={testId}
      className={cn('flex flex-col items-center justify-center gap-3 py-8 text-center', className)}
    >
      <ShogunSpinner size="md" />
      <span className="text-sm text-muted-foreground">{label}</span>
    </div>
  );
}
